import { useEffect, useRef, useState } from "react";
import { Box } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Spacing } from "goi_common";
import { BaseStack, baseColors } from "goi_common/src/design-system";

import type { GuestCostRange } from "../utils/parlorIntroCost";

/** 금액 · 막대가 함께 쓰는 움직임. 빠르게 출발해 천천히 멈춘다 */
const MOTION_MS = 450;
const MOTION = `${MOTION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;
const easeOutQuint = (t: number) => 1 - Math.pow(1 - t, 5);

/**
 * 값이 바뀌면 이전 값에서 새 값까지 MOTION_MS 동안 부드럽게 따라간다.
 * 금액 글자와 막대 위치를 같은 숫자로 그려서 둘이 어긋나지 않게 움직인다.
 */
const useTweenedNumber = (target: number) => {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const valueRef = useRef(target);
  useEffect(() => {
    // 처음 그릴 때처럼 값이 같으면 움직일 게 없다
    if (valueRef.current === target) return;
    fromRef.current = valueRef.current;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / MOTION_MS);
      const next = fromRef.current + (target - fromRef.current) * easeOutQuint(t);
      valueRef.current = next;
      setValue(next);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return Math.round(value);
};

/** E5 질문 화면 위 예상 비용 카드. 고른 답에 따라 금액과 구간이 부드럽게 바뀐다 */
export default function CostRangeCard({
  range,
  selected,
}: {
  range: GuestCostRange;
  /** 지금 보여 줄 구간. 없으면 금액 대신 안내 문구 + 꽉 찬 연한 막대 */
  selected: { lo: number; hi: number } | null;
}) {
  const { minManwon: min, maxManwon: max } = range;
  const lo = useTweenedNumber(selected?.lo ?? min);
  const hi = useTweenedNumber(selected?.hi ?? max);
  const pos = (v: number) => (max === min ? 0 : ((v - min) / (max - min)) * 100);
  const isPicked = !!selected;
  const amount =
    lo === hi
      ? `약 ${lo.toLocaleString()}만 원`
      : `약 ${lo.toLocaleString()}~${hi.toLocaleString()}만 원`;

  // 주황 배경 카드 한 장으로 짧게(2026-11-03 주완: 더 잘 보이고 세로가 길어지지 않게)
  return (
    <Box px="20px" py="12px">
      {/* 다음 화면(④)의 큰 카드와 같은 layoutId. 「다음」 을 누르면 이 카드가 그 자리에서 커진다 */}
      {/* 돌아올 때(비용 구성 → 질문)는 움직임 없이 바로. 내려가 커지는 움직임은 비용 구성 쪽 transition 만 쓴다(2026-11-04 주완) */}
      <motion.div layoutId="e5-cost-card" transition={{ duration: 0 }} style={{ borderRadius: 12 }}>
        <BaseStack
          width="100%"
          px="16px"
          pt="14px"
          pb="12px"
          borderRadius="12px"
          bg={isPicked ? baseColors.orange_100 : baseColors.background_accent_1}
          transition={`background-color ${MOTION}`}
        >
          {/* 고르기 전에는 금액 대신 안내만. 전체 범위(무빈소~200명)는 너무 넓고 첫 화면 금액과도 달라
              잘못 읽혔다. 막대는 연한 주황으로 꽉 채워 두고, 고르면 그 구간으로 줄어든다(2026-11-04 주완 C안).
              막대 아래 최저 · 최고는 그대로 둔다 */}
          <BaseStack direction="horizontal" width="100%" alignItems="baseline">
            {isPicked ? (
              <>
                <Box className="label_1_r" color={baseColors.label_alternative_1}>
                  우리 가족 예상 비용
                </Box>
                <Box flex={1} />
                {/* 18px 는 커 보여서 16px 볼드(2026-11-03 주완) */}
                <Box
                  className="body_1_normal_sb"
                  fontWeight={700}
                  color={baseColors.primary_orange}
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {amount}
                </Box>
              </>
            ) : (
              <Box className="body_1_normal_sb" color={baseColors.label_alternative_1}>
                조문객 수를 고르면 예상 비용이 나와요
              </Box>
            )}
          </BaseStack>
          {/* 금액 줄과 막대 사이(14 → 20px, 주완) */}
          <Spacing gap={20} />
          <Box
            position="relative"
            width="100%"
            height="6px"
            borderRadius="999px"
            bg={baseColors.background_normal}
          >
            <motion.div
              style={{
                position: "absolute",
                left: `${pos(lo)}%`,
                width: `${Math.max(pos(hi) - pos(lo), 1.5)}%`,
                height: "100%",
                borderRadius: 999,
                background: isPicked ? baseColors.primary_orange : baseColors.orange_150,
                transition: `background-color ${MOTION}`,
              }}
            />
            {/* 범위 시작 · 끝 표시. 원 대신 세로 막대라 끌 수 있는 손잡이로 보이지 않는다 */}
            {isPicked &&
              [lo, hi].map((v, i) => (
                <Box
                  key={i}
                  position="absolute"
                  left={`${pos(v)}%`}
                  top="50%"
                  width="3px"
                  height="14px"
                  borderRadius="2px"
                  bg={baseColors.primary_orange}
                  transform="translate(-50%, -50%)"
                />
              ))}
          </Box>
          <Spacing gap={6} />
          {/* 막대 양 끝(최저 · 최고)은 고르기 전에도 보여 준다(2026-11-04 주완) */}
          <BaseStack direction="horizontal" width="100%">
            <Box className="caption_2_r" color={baseColors.label_assistive_1}>
              {min.toLocaleString()}만
            </Box>
            <Box flex={1} />
            <Box className="caption_2_r" color={baseColors.label_assistive_1}>
              {max.toLocaleString()}만
            </Box>
          </BaseStack>
        </BaseStack>
      </motion.div>
    </Box>
  );
}
